/** Upgrade page — plan selection and upgrade. */

import { useState } from "react";
import { api } from "@/api/client";
import { useAuthStore } from "@/stores/auth";
import { Button } from "@/components/ui/Button";
import type { User } from "@/types";

const PLANS = [
  {
    id: "free" as const,
    name: "Free",
    price: "$0",
    period: "forever",
    features: [
      "1 competition",
      "Unlimited judges",
      "Real-time scoring",
      "OBS overlay",
      "Training & certification",
    ],
    limitations: [
      '"Scores by RALEY" watermark on overlay',
    ],
  },
  {
    id: "pro" as const,
    name: "Pro",
    price: "$29",
    period: "/month",
    features: [
      "Unlimited competitions",
      "Unlimited judges",
      "Real-time scoring",
      "OBS overlay",
      "Training & certification",
      "No watermark on overlay",
    ],
    limitations: [],
  },
];

export function UpgradePage() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const [upgrading, setUpgrading] = useState<string | null>(null);

  const currentPlan = user?.plan ?? "free";

  const handleSelectPlan = async (planId: string) => {
    if (planId === currentPlan) return;
    setUpgrading(planId);
    try {
      const updated = await api.put<User>("/api/auth/plan", { plan: planId });
      setUser(updated);
    } finally {
      setUpgrading(null);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Upgrade</h1>
          <p className="page-subtitle">Choose the plan that fits your needs.</p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, maxWidth: 700 }}>
        {PLANS.map((plan) => {
          const isCurrent = plan.id === currentPlan;
          const isHighlighted = plan.id === "pro";

          return (
            <div
              key={plan.id}
              style={{
                border: `2px solid ${isHighlighted ? "#f59e0b" : "var(--c-border)"}`,
                borderRadius: 12,
                padding: 28,
                display: "flex",
                flexDirection: "column",
                background: isHighlighted ? "rgba(245, 158, 11, 0.04)" : "var(--c-surface)",
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 700 }}>{plan.name}</div>
              <div style={{ marginTop: 8, display: "flex", alignItems: "baseline", gap: 4 }}>
                <span style={{ fontSize: 36, fontWeight: 800 }}>{plan.price}</span>
                <span className="text-sm text-muted">{plan.period}</span>
              </div>

              <div style={{ marginTop: 20, flex: 1 }}>
                {plan.features.map((f) => (
                  <div key={f} style={{ fontSize: 13, padding: "4px 0", display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ color: "var(--c-success, #22c55e)" }}>&#10003;</span>
                    {f}
                  </div>
                ))}
                {plan.limitations.map((l) => (
                  <div key={l} style={{ fontSize: 13, padding: "4px 0", display: "flex", gap: 8, alignItems: "center", color: "var(--c-text-muted)" }}>
                    <span style={{ color: "var(--c-text-muted)" }}>&#8212;</span>
                    {l}
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 20 }}>
                {isCurrent ? (
                  <Button variant="secondary" size="sm" disabled style={{ width: "100%" }}>
                    Current Plan
                  </Button>
                ) : (
                  <Button
                    style={{
                      width: "100%",
                      ...(isHighlighted ? { background: "linear-gradient(135deg, #f59e0b, #d97706)", border: "none" } : {}),
                    }}
                    loading={upgrading === plan.id}
                    onClick={() => handleSelectPlan(plan.id)}
                  >
                    {plan.id === "free" ? "Downgrade to Free" : "Upgrade to Pro"}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
